import React, { ReactNode } from 'react';
import './SettingsCard.css';

interface SettingsCardProps {
  title: string;
  /** Optional one-line hint under the title. */
  description?: string;
  /**
   * Span every column of the settings grid. For groups that genuinely need the
   * width — the notification category x channel matrix, for instance.
   */
  wide?: boolean;
  /** Pinned to the bottom of the card, above a separator (e.g. a Save button). */
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * One group of settings, as a card in the `.settings-grid`.
 *
 * The grid is `repeat(auto-fit, minmax(...))`, so small groups pack side by
 * side and fill the width instead of forming one narrow column with an empty
 * screen beside it. `wide` opts a card out into a full-width row.
 */
const SettingsCard: React.FC<SettingsCardProps> = ({
  title,
  description,
  wide = false,
  actions,
  children,
}) => (
  <section
    className={`settings-card${wide ? ' settings-card--wide' : ''}`}
    data-testid="settings-card"
    data-wide={wide ? 'true' : 'false'}
  >
    <header className="settings-card__head">
      <h3 className="settings-card__title">{title}</h3>
      {description && <p className="settings-card__desc">{description}</p>}
    </header>

    <div className="settings-card__body">{children}</div>

    {actions && <footer className="settings-card__actions">{actions}</footer>}
  </section>
);

export default SettingsCard;
